import { PartitionOutlined } from "@ant-design/icons";
import { useCallback, useEffect } from "react";
import { ControlButton, Controls, useReactFlow, useViewport } from "reactflow";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./react-flow-controls.scss";

interface IReactFlowControlsProps {
  showAutoArrange?: boolean;
  onAutoArrange?: () => void;
  showMiniMapToggle?: boolean;
  isMiniMapVisible?: boolean;
  onToggleMiniMap?: () => void;
}

const SharedReactFlowControls = ({
  showAutoArrange = false,
  onAutoArrange,
  showMiniMapToggle = false,
  isMiniMapVisible = true,
  onToggleMiniMap
}: IReactFlowControlsProps) => {
  const { fitView, zoomTo } = useReactFlow();
  const { zoom } = useViewport();

  const snapZoomToStep = useCallback((zoomLevel: number) => {
    const snappedPercent = Math.round((zoomLevel * 100) / 5) * 5;
    const clampedPercent = Math.min(200, Math.max(10, snappedPercent));
    return clampedPercent / 100;
  }, []);

  useEffect(() => {
    const snappedZoom = snapZoomToStep(zoom);
    if (Math.abs(snappedZoom - zoom) > 0.0001) {
      zoomTo(snappedZoom);
    }
  }, [zoom, zoomTo, snapZoomToStep]);

  const currentZoom = Math.round(snapZoomToStep(zoom) * 100);

  const getSelectedZoomKey = useCallback((zoomLevel: number) => {
    const zoomPercent = Math.round(zoomLevel * 100);
    const key = String(zoomPercent);
    return ["50", "100", "125"].includes(key) ? key : null;
  }, []);

  const selectedZoom = getSelectedZoomKey(snapZoomToStep(zoom)) || String(currentZoom);

  const handleZoomIn = useCallback(() => {
    const newZoom = snapZoomToStep(zoom + 0.1); // 10% step, snapped to 5%
    zoomTo(newZoom);
  }, [zoom, zoomTo, snapZoomToStep]);

  const handleZoomOut = useCallback(() => {
    const newZoom = snapZoomToStep(zoom - 0.1); // 10% step, snapped to 5%
    zoomTo(newZoom);
  }, [zoom, zoomTo, snapZoomToStep]);

  const handleZoomSelect = useCallback(
    (key: number | string | null) => {
      const zoomMap: { [key: string | number]: number } = {
        "50": 0.5,
        "100": 1,
        "125": 1.25
      };
      const zoomLevel = zoomMap[key as string | number];
      if (zoomLevel) {
        zoomTo(snapZoomToStep(zoomLevel));
      }
    },
    [zoomTo, snapZoomToStep]
  );

  return (
    <Controls className="kuika-react-flow-controls" showFitView={false} showInteractive={false} showZoom={false}>
      <div className="kuika-react-flow-controls__container">
        <div className="kuika-react-flow-controls__btn-container">
          <ControlButton onClick={handleZoomIn} style={{ borderRadius: "var(--radius-s) 0 0 var(--radius-s)" }}>
            <KuikaSvg type="common" name="plus" width={16} height={16} />
          </ControlButton>
        </div>

        <div className="kuika-react-flow-controls__container">
          <KuikaDropdownMenu
            trigger="click"
            selected={selectedZoom}
            setSelected={handleZoomSelect}
            overlayClassName="kuika-react-flow-controls__zoom-dropdown"
            options={[
              { key: "50", label: "50%", showSelectedIcon: false },
              { key: "100", label: "100%", showSelectedIcon: false },
              { key: "125", label: "125%", showSelectedIcon: false }
            ]}
            closeOnSelect
            getPopupContainer={(e) => e?.parentElement || document.body}
          >
            <KuikaTooltip content="Zoom Level">
              <div className="kuika-react-flow-controls__btn-container">
                <ControlButton
                  style={{
                    width: "60px",
                    padding: "4px",
                    borderRadius: "var(--radius-s)"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "12px",
                      fontWeight: "500"
                    }}
                  >
                    <span>{currentZoom}%</span>
                    <KuikaSvg type="arrows" name="chevron-down" width={14} height={14} />
                  </div>
                </ControlButton>
              </div>
            </KuikaTooltip>
          </KuikaDropdownMenu>
        </div>

        <div className="kuika-react-flow-controls__btn-container">
          <ControlButton onClick={handleZoomOut}>
            <KuikaSvg type="common" name="minus" width={16} height={16} />
          </ControlButton>
        </div>

        <KuikaTooltip content="Fit to view">
          <div className="kuika-react-flow-controls__btn-container">
            <ControlButton
              onClick={() => fitView()}
              style={!showAutoArrange ? { borderRadius: "0 var(--radius-s) var(--radius-s) 0" } : undefined}
            >
              <KuikaSvg type="common" name="fit-to-view" width={16} height={16} />
            </ControlButton>
          </div>
        </KuikaTooltip>

        {showAutoArrange && !!onAutoArrange && (
          <KuikaTooltip content="Auto Arrange">
            <div className="kuika-react-flow-controls__btn-container">
              <ControlButton onClick={onAutoArrange} style={{ borderRadius: "0 var(--radius-s) var(--radius-s) 0" }}>
                <PartitionOutlined />
              </ControlButton>
            </div>
          </KuikaTooltip>
        )}

        {showMiniMapToggle && !!onToggleMiniMap && (
          <KuikaTooltip content={isMiniMapVisible ? "Hide Mini Map" : "Show Mini Map"}>
            <div className="kuika-react-flow-controls__btn-container">
              <ControlButton
                onClick={onToggleMiniMap}
                className={`kuika-react-flow-controls__mini-map-btn ${
                  isMiniMapVisible ? "kuika-react-flow-controls__mini-map-btn--active" : ""
                }`}
              >
                <KuikaSvg type="common" name="map" width={14} height={14} />
              </ControlButton>
            </div>
          </KuikaTooltip>
        )}
      </div>
    </Controls>
  );
};

export default SharedReactFlowControls;
